import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, matchPath, useNavigate } from "react-router-dom";
import "./assets/css/app.css";
import Login from "./Pages/Auth/Login";
import Home from "./Pages/shelter-bed/Home";
import ForgotPassword from "./Pages/Auth/ForgotPassword";
import Header from "./Components/Header";
import Footer from "./Components/Footer";
import Navbar from "./Components/Navbar";
import KeycloakProvider, { useKeycloak } from "./utils/KeycloakProvider";
import { FeaturesProvider, useFeature } from "./hooks/useFeature";
import NewRequest from "./Pages/shelter-bed/new-request";
import RequestSent from "./Pages/shelter-bed/bed-request/request-sent";
import RequestReceive from "./Pages/shelter-bed/bed-request/request-receive";
import ReceiveDetail from "./Pages/shelter-bed/bed-request/request-receive/receive-detail";
import SentDetail from "./Pages/shelter-bed/bed-request/request-sent/sent-detail";
import MemberPage from "./Pages/shelter-bed/Member";
import Collaborative from "./Pages/shelter-bed/Collaborative";
import SettingsPage from "./Pages/shelter-bed/Settings";
import MyAccountPage from "./Pages/shelter-bed/Account/my-account";
import CollaborativeDetails from "./Pages/shelter-bed/Collaborative/CollaborativeDetails";
import CreateNewUser from "./Pages/shelter-bed/Account/new-user";
import LocateShelterDashboard from "./Pages/shelter-bed/Dashboard";
import FundAHotelDashboard from "./Pages/fund-hotel/Dashboard";
import NeedsHome from "./Pages/fund-hotel/needs";
// import 'https://cdn.jsdelivr.net/npm/flowbite@3.1.1/dist/flowbite.min.js';
import DonationsHome from "./Pages/fund-hotel/donations";
import DonorsHome from "./Pages/fund-hotel/donors";
import UserDetails from "./Pages/shelter-bed/Account/user-details";
import PrivacyPolicy from "./Pages/privacy-policy";
import NeedsDetails from "./Pages/fund-hotel/needs/need-details";
import NeedsDetailsEdit from "./Pages/fund-hotel/needs/edit-needs";
import DontationsDetails from "./Pages/fund-hotel/donations/donations-details";
import DonorsDetails from "./Pages/fund-hotel/donors/donors-details";
import TermsAndCondition from "./Pages/terms-and-condition";
import ToastNotification from "./Components/ToastNotification";
import useUserData from "./hooks/useUserData";
import ModalDailog from "./Components/Modal";
import createAxiosInstance from "./utils/axiosInstance";
import { API_ENDPOINTS } from "./utils/api";

const AppWithLocation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const keycloak = useKeycloak(); // Access Keycloak context
  const [loading, setLoading] = useState<boolean>(true); // Track loading status
  const [isOpen, setIsOpen] = useState(false);
  const [accessForWhat, setAccessForWhat] = useState<string>(""); 
  const userData = useUserData();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const axiosInstance = createAxiosInstance();

  const triggerPopup = () => setIsModalOpen(true);

  const closeModal = async () => {
    setIsModalOpen(false);
    if (userData && userData.id) {
      try {
        await axiosInstance.put(
          `${API_ENDPOINTS.BEDFINDER.GETUSERS }/${userData.id}`,
          { hasSeenNewUiPopup: true }
        );
        if (userData) userData.hasSeenNewUiPopup = true; // update locally
      } catch (error) {
        console.log(error);
      }
    }
  };

  const hideHeaderRoutes = ["/login", "/forgot-password"];
  const hideNavbarRoutes = ["/"];
  const bedfinderRoutes = [
    "/bedfinder",
    "/bedfinder/new-request",
    "/bedfinder/sent-request",
    "/bedfinder/beds-needed",
    "/bedfinder/my-requests",
    "/bedfinder/beds-needed/review/:requestId",
    "/bedfinder/my-requests/review/:requestId",
    "/bedfinder/members",
    "/bedfinder/collaborative",
    "/bedfinder/collaborative/:groupId",
    "/bedfinder/settings"
  ];

  const commonRoutes = ["/privacy", "/terms", "my-account", "/my-account/new-user", "/my-account/user-details/:userId"];
  
  // Call hooks outside useEffect
  const bedfinder = useFeature("bedfinder");
  const safenight = useFeature("safenight");

  // Handle Keycloak authentication status
  useEffect(() => {
    if (keycloak.authenticated !== undefined) {
      setAuthenticated(keycloak.authenticated);// Update feature flags
      // Redirect based on feature flags
      if (keycloak.authenticated) {
        if (!bedfinder && safenight) {
          setAccessForWhat("safenight");
          navigate("/safenight", { replace: true });
        } 
        if (bedfinder && !safenight) {
          setAccessForWhat("bedfinder");
          navigate("/bedfinder", { replace: true });
        }
        if (bedfinder && safenight) {
          setAccessForWhat("bedfinder"); // Default to bedfinder if both features are enabled
        }
      }
      setLoading(false); // Stop loading once Keycloak status is determined
    }
  }, [keycloak, bedfinder, safenight]); // Re-run this effect when Keycloak object changes

  // Show loading screen until Keycloak is initialized
  if (loading) {
    return <div>Loading...</div>;
  }

  const isBedfinderRoute = bedfinderRoutes.some((route) =>
    matchPath(route, location.pathname)
  );

  const isCommonRoute = commonRoutes.some((route) =>
    matchPath(route, location.pathname)
  );

  return (
    <div className="App">
      {/* Conditionally render header and navbar */}
      {!hideHeaderRoutes.includes(location.pathname) && <Header isOpen={isOpen} setIsOpen={setIsOpen} />}
      {(!hideHeaderRoutes.includes(location.pathname) && !hideNavbarRoutes.includes(location.pathname)) && <Navbar theme={isBedfinderRoute ? "bedfinder" : isCommonRoute ? accessForWhat === "bedfinder" ? "bedfinder" : "safeNight" : "safeNight"} isOpen={isOpen} />}
      <div className={`content-block ${location.pathname === '/bedfinder/new-request' ? 'bg-white' : ''}`}>
        <ToastNotification />
        <ModalDailog
          isOpen={isModalOpen}
          onClose={closeModal}
          title="New look same great features!"
          isFooterAvailable={false}
          modalClassName="lg:max-w-lg"
        >
          <div className="text-center">
            <p className="mb-4 text-sm 2xl:text-base text-gray-600">
              We've updated the Safe Shelter Collaborative platform. The changes you'll see are color and design and a few minor updates in between. We hope you like the glow up!
            </p>
            <button
              onClick={closeModal}
              className="bg-blue-600 text-white px-8 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Let me in
            </button>
          </div>
        </ModalDailog>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          {authenticated && (
            <>
              <Route path="/" element={<Home userData={userData} triggerPopup={triggerPopup} />} />
              <Route path="/bedfinder" element={<LocateShelterDashboard userData={userData} triggerPopup={triggerPopup} />} />
              <Route path="/bedfinder/new-request" element={<NewRequest />} />
              <Route path="/bedfinder/my-requests" element={<RequestSent />} />
              <Route path="/bedfinder/beds-needed" element={<RequestReceive />} />
              <Route path="/bedfinder/beds-needed/review/:requestId" element={<ReceiveDetail />} />
              <Route path="/bedfinder/my-requests/review/:requestId" element={<SentDetail />} />
              <Route path="/bedfinder/members" element={<MemberPage />} />
              <Route path="/bedfinder/collaborative" element={<Collaborative />} />
              <Route path="/bedfinder/collaborative/:groupId" element={<CollaborativeDetails />} />
              <Route path="/bedfinder/settings" element={<SettingsPage />} />
              <Route path="/my-account" element={<MyAccountPage />} />
              <Route path="/my-account/new-user" element={<CreateNewUser />} />
              <Route path="/my-account/user-details/:userId" element={<UserDetails />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsAndCondition />} />
              <Route path="/safenight" element={<FundAHotelDashboard userData={userData} triggerPopup={triggerPopup} />} />
              <Route path="/safenight/needs" element={<NeedsHome />} />
              <Route path="/safenight/needs/need-details/:needId" element={<NeedsDetails />} />
              <Route path="/safenight/needs/need-details/edit/:needId" element={<NeedsDetailsEdit />} />
              <Route path="/safenight/donations" element={<DonationsHome />} />
              <Route path="/safenight/donations/donation-detail/:donationId" element={<DontationsDetails />} />
              <Route path="/safenight/donors" element={<DonorsHome />} />
              <Route path="/safenight/donors/donor-detail/:donorId" element={<DonorsDetails />} />

            </>
          )}
        </Routes>
      </div>
      {!hideHeaderRoutes.includes(location.pathname) && <Footer />}
    </div>
  );
};

function App() {
  return (
    <KeycloakProvider>
      <FeaturesProvider>
        <BrowserRouter>
          <AppWithLocation />
        </BrowserRouter>
      </FeaturesProvider>
    </KeycloakProvider>
  );
}

export default App;
